'use client';
import { useController, useFormContext } from 'react-hook-form';

import { faMagicWandSparkles, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import { ExpandedChat } from 'components/WYSIWYGChat';
import { get, uniqBy } from 'lodash';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-quill-new/dist/quill.snow.css';
import { Group, Panel, Separator } from 'react-resizable-panels';
import KiLabel from '~/components/ui/KiLabel';
import TextTemplateManager from '../../../TextTemplate/TextTemplateManager';
import { FormInputProps } from '../props';
import { Placeholder } from './types';

interface Props extends FormInputProps {
    placeholders?: Placeholder[];
    disabled?: boolean;
    clubSlug?: string;
    hideTemplates?: boolean;
}

export const FormWYSIWYG = ({
    label,
    name,
    clubSlug,
    hideTemplates,
    placeholders: _placeholders = [],
    disabled
}: Props) => {
    const { t } = useTranslation('form');
    const QuillNoSSRWrapper = useMemo(
        () =>
            dynamic(
                async () => {
                    const { default: ReactQuill } = await import('react-quill-new');
                    const { default: PlaceholderBlot } =
                        await import('components/Form/next/FormWYSIWYG/PlaceholderBlot');

                    ReactQuill.Quill.register(PlaceholderBlot as any);

                    const QuillWithRef = ({
                        forwardedRef,
                        ...props
                    }: { [x: string]: any } & { forwardedRef?: any; placeholders?: Placeholder[] }) => (
                        <ReactQuill ref={forwardedRef} {...props} />
                    );

                    return QuillWithRef;
                },
                {
                    ssr: false,
                    loading: () => <div className="tw-h-[200px] tw-w-full tw-animate-pulse tw-bg-accents-1" />
                }
            ),
        []
    );

    const wrapperRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    const {
        control,
        formState: { errors }
    } = useFormContext();
    const {
        field: { onChange, onBlur, value }
    } = useController({ name, control });

    const error = get(errors, name);
    const quillRef = useRef<any>(undefined);

    const { addModal, removeModal } = useModalManager((state) => ({
        addModal: state.addModal,
        removeModal: state.removeModal
    }));

    const isInvalid = Boolean(error);
    const [isChatExpanded, setIsChatExpanded] = useState<boolean>(false);
    const [editorHeight, setEditorHeight] = useState<number>();

    const wrapperClassName = `wysiwyg-wrapper${isInvalid ? ' wysiwyg-wrapper--invalid' : ''}`;

    const extractPlaceholders = () => {
        const extractedPlaceholders = [];
        if (value && (!_placeholders || _placeholders.length <= 0)) {
            const testString: string = value;
            const regex = new RegExp('<span class="ql-placeholder" data-name="(.*?)" data-title="(.*?)">', 'g');
            const matches = testString.matchAll(regex);
            let current = matches.next();
            while (!current.done) {
                extractedPlaceholders.push({ name: current.value[1], title: current.value[2] });
                current = matches.next();
            }
        }
        return extractedPlaceholders;
    };
    const placeholders = uniqBy([..._placeholders, ...extractPlaceholders()], 'name');

    const placeholdersRef = useRef(placeholders);
    placeholdersRef.current = placeholders;

    const handlePropertyPlaceholders = useCallback((name: string) => {
        const placeholder = placeholdersRef.current.find((p) => p.name === name);
        const editor = quillRef.current.getEditor();

        if (editor) {
            const range = editor.getSelection();
            const position = range ? range.index : 0;
            editor.insertEmbed(position, 'placeholder', placeholder, 'user');
            editor.setSelection(position + 1, 0);
            editor.insertText(position + 1, ' ');
        }
    }, []);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    ...(placeholders?.length > 0 ? [[{ placeholder: placeholders?.map(({ name }) => name) }]] : []),
                    [{ header: [1, 2, 3, 4, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                    ['link'],
                    [{ color: [] }, { background: [] }],
                    [{ align: [] }],
                    ['clean']
                ],
                handlers: {
                    placeholder: handlePropertyPlaceholders
                }
            },
            clipboard: {
                matchVisual: false
            }
        }),
        [placeholders, handlePropertyPlaceholders]
    );

    useEffect(() => {
        const container = editorContainerRef.current;
        if (!container) return;

        const updateHeight = () => setEditorHeight(container.getBoundingClientRect().height);
        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        const initDropdown = () => {
            if (wrapperRef.current) {
                const placeholderLabel = wrapperRef.current.querySelector('.ql-placeholder .ql-picker-label');
                if (placeholders?.length > 0 && placeholderLabel) {
                    const placeholderPickerItems = Array.prototype.slice.call(
                        wrapperRef.current.querySelectorAll('.ql-placeholder .ql-picker-item')
                    );
                    placeholderPickerItems.forEach(
                        (item) => (item.textContent = placeholders.find((p) => p.name === item.dataset.value)?.title)
                    );
                    wrapperRef.current.querySelector('.ql-placeholder .ql-picker-label').innerHTML =
                        t('wysiwyg.placeholderVariable');
                }
            } else {
                setTimeout(initDropdown, 200);
            }
        };

        let timeout = setTimeout(initDropdown, 200);
        return () => clearTimeout(timeout);
    }, [placeholders, t]);

    const handleShowTemplateManager = () => {
        addModal({
            title: t('textTemplates.manageTitle'),
            size: 50,
            content: (id) => (
                <TextTemplateManager
                    clubSlug={clubSlug}
                    currentText={value}
                    onSelectTemplate={(text) => {
                        onChange(text);
                        removeModal(id);
                    }}
                />
            )
        });
    };

    const placeholderKey = useMemo(() => placeholders.map((p) => `${p.name}:${p.title}`).join('|'), [placeholders]);

    return (
        <div className={wrapperClassName} ref={wrapperRef}>
            {label && (
                <label className={`tw-mb-1 tw-pb-1 tw-text-[14px] ${isInvalid ? 'tw-text-error' : 'tw-text-black'}`}>
                    {label}
                </label>
            )}
            <div className="tw-flex tw-flex-col tw-gap-2">
                {!hideTemplates && (
                    <Button
                        block
                        size="xs"
                        color="light"
                        onPress={handleShowTemplateManager}
                        icon={<FontAwesomeIcon icon={faStickyNote} />}
                    >
                        {t('wysiwyg.manageTemplates')}
                    </Button>
                )}
            </div>
            <div className="tw-relative tw-mt-2 tw-overflow-hidden tw-rounded-2xl tw-bg-[#f4f4f5]">
                {!isChatExpanded && (
                    <Button
                        size="sm"
                        flat
                        onPress={() => setIsChatExpanded((prev) => !prev)}
                        className="tw-absolute tw-right-2 tw-top-2 tw-z-50"
                        icon={<FontAwesomeIcon icon={faMagicWandSparkles} />}
                    >
                        <KiLabel className="">{t('wysiwyg.aiAssistant')}</KiLabel>
                    </Button>
                )}
                <Group orientation="horizontal" className="tw-w-full">
                    <Panel minSize={40}>
                        <div className="tw-relative" ref={editorContainerRef}>
                            {disabled && (
                                <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-40 tw-rounded-xl tw-bg-accents-5/80">
                                    <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-text-accents-7">
                                        {t('wysiwyg.unavailable')}
                                    </div>
                                </div>
                            )}

                            <QuillNoSSRWrapper
                                key={placeholderKey}
                                readOnly={disabled}
                                forwardedRef={quillRef}
                                modules={modules}
                                theme="snow"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                            />
                        </div>
                    </Panel>

                    {isChatExpanded && (
                        <>
                            <Separator className="tw-my-4 tw-w-2 tw-cursor-col-resize tw-rounded-xl tw-bg-accents-3 hover:tw-bg-accents-5" />

                            <Panel defaultSize="30%" minSize="25%" maxSize="50%">
                                <div
                                    className="tw-r-r tw-h-full"
                                    style={editorHeight ? { height: editorHeight } : undefined}
                                >
                                    <ExpandedChat name={name} onClose={() => setIsChatExpanded(false)} />
                                </div>
                            </Panel>
                        </>
                    )}
                </Group>
            </div>
            {isInvalid && <p className="tw-ml-2 tw-text-[10px] tw-text-error">{error?.message as string}</p>}
            <style jsx>{`
                .wysiwyg-wrapper {
                    width: 100%;
                    margin-bottom: 0.5rem;
                }

                .wysiwyg-wrapper :global(.ql-editor) {
                    min-height: 260px;
                }

                /* ... rest of your styles ... */

                .wysiwyg-wrapper :global(.ql-editor strong),
                .wysiwyg-wrapper :global(.ql-editor b) {
                    font-weight: 600;
                }
                .wysiwyg-wrapper :global(.ql-editor span.ql-placeholder),
                .wysiwyg-wrapper :global(.ql-editor span.ql-placeholder-embed) {
                    padding: 0 0.5rem;
                    margin: 0 2px;
                    border-radius: 6px;
                    background-color: #c1c8cd;
                }

                .wysiwyg-wrapper :global(.ql-toolbar),
                .wysiwyg-wrapper :global(.ql-container) {
                    background-color: #eceef0;
                    border-color: #eceef0;
                }

                .wysiwyg-wrapper :global(.ql-toolbar button.ql-active),
                .wysiwyg-wrapper :global(.ql-toolbar button:hover),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-label:hover),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-item:hover) {
                    color: #38c172;
                }

                .wysiwyg-wrapper :global(.ql-toolbar button.ql-active .ql-stroke),
                .wysiwyg-wrapper :global(.ql-toolbar button:hover .ql-stroke),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-label:hover .ql-stroke),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-item:hover .ql-stroke) {
                    stroke: #38c172;
                }

                .wysiwyg-wrapper :global(.ql-toolbar button.ql-active .ql-fill),
                .wysiwyg-wrapper :global(.ql-toolbar button:hover .ql-fill),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-label:hover .ql-fill),
                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker-item:hover .ql-fill) {
                    fill: #38c172;
                }

                .wysiwyg-wrapper :global(.ql-toolbar .ql-picker .ql-picker-options) {
                    max-height: 200px;
                    overflow: scroll;
                }

                .wysiwyg-wrapper--invalid :global(.ql-toolbar),
                .wysiwyg-wrapper--invalid :global(.ql-container) {
                    background-color: #fbeded;
                    border-color: #fbeded;
                }

                .wysiwyg-wrapper--invalid :global(.invalidFeedback) {
                    display: block;
                }
            `}</style>
        </div>
    );
};
